import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import {
  Bell,
  Plus,
  Trash2,
  Mail,
  Phone,
  User,
  Settings,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Recipient = {
  id: number;
  name: string;
  email: string;
  phone: string;
  email_notifications: boolean;
  phone_notifications: boolean;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function OpsSettings() {
  const { token } = useAuth();

  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add form
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newEmailNotif, setNewEmailNotif] = useState(true);
  const [newPhoneNotif, setNewPhoneNotif] = useState(false);
  const [adding, setAdding] = useState(false);

  // Delete confirmation
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token],
  );

  // -----------------------------------------------------------------------
  // Data fetching
  // -----------------------------------------------------------------------

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch("/api/ops/settings/work-order-notifications", { headers });
      if (!res.ok) throw new Error(`Failed to load settings (${res.status})`);
      const data = await res.json();
      setRecipients(data.recipients ?? data ?? []);
    } catch (err: any) {
      setError(err?.message || "Failed to load notification settings");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // -----------------------------------------------------------------------
  // Add recipient
  // -----------------------------------------------------------------------

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim() || !newEmail.trim()) return;
    setAdding(true);
    setError(null);
    try {
      const res = await apiFetch("/api/ops/settings/work-order-notifications", {
        method: "POST",
        headers,
        body: JSON.stringify({
          name: newName.trim(),
          email: newEmail.trim(),
          phone: newPhone.trim() || null,
          email_notifications: newEmailNotif,
          phone_notifications: newPhoneNotif,
        }),
      });
      if (!res.ok) throw new Error("Failed to add recipient");
      setNewName("");
      setNewEmail("");
      setNewPhone("");
      setNewEmailNotif(true);
      setNewPhoneNotif(false);
      setShowAdd(false);
      await loadData();
    } catch (err: any) {
      setError(err?.message || "Failed to add recipient");
    } finally {
      setAdding(false);
    }
  };

  // -----------------------------------------------------------------------
  // Toggle notification preference
  // -----------------------------------------------------------------------

  const toggleNotification = async (id: number, field: "email_notifications" | "phone_notifications", value: boolean) => {
    try {
      const res = await apiFetch(`/api/ops/settings/work-order-notifications/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ [field]: value }),
      });
      if (!res.ok) throw new Error("Failed to update preference");
      setRecipients((prev) =>
        prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
      );
    } catch (err: any) {
      setError(err?.message || "Failed to update notification preference");
    }
  };

  // -----------------------------------------------------------------------
  // Delete recipient
  // -----------------------------------------------------------------------

  const handleDelete = async (id: number) => {
    try {
      const res = await apiFetch(`/api/ops/settings/work-order-notifications/${id}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) throw new Error("Failed to delete recipient");
      setDeletingId(null);
      await loadData();
    } catch (err: any) {
      setError(err?.message || "Failed to delete recipient");
    }
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <Settings size={28} className="text-[#264027]" />
            Ops Settings
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage work order notification recipients
          </p>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-700 dark:text-red-400">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Notification Recipients */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <Bell size={18} className="text-[#264027]" />
            <h2 className="text-sm font-bold text-gray-900 dark:text-white">
              Work Order Notification Recipients
            </h2>
            <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 rounded-full px-2 py-0.5 font-semibold">
              {recipients.length}
            </span>
          </div>
          <button
            onClick={() => setShowAdd(!showAdd)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#264027] hover:bg-[#1a2e1b] text-white rounded-lg text-xs font-semibold transition-colors"
          >
            <Plus size={14} /> Add Recipient
          </button>
        </div>

        {/* Add form */}
        {showAdd && (
          <form onSubmit={handleAdd} className="p-5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Name *
                </label>
                <div className="relative">
                  <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Full name"
                    required
                    className="w-full pl-9 pr-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Email *
                </label>
                <div className="relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="email@example.com"
                    required
                    className="w-full pl-9 pr-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Phone
                </label>
                <div className="relative">
                  <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="tel"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    placeholder="(602) 555-0000"
                    className="w-full pl-9 pr-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newEmailNotif}
                  onChange={(e) => setNewEmailNotif(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-[#264027] focus:ring-[#264027]"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Email notifications</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newPhoneNotif}
                  onChange={(e) => setNewPhoneNotif(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-[#264027] focus:ring-[#264027]"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Phone notifications</span>
              </label>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={adding || !newName.trim() || !newEmail.trim()}
                className="px-4 py-2 bg-[#264027] hover:bg-[#1a2e1b] disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors"
              >
                {adding ? "Adding..." : "Add Recipient"}
              </button>
              <button
                type="button"
                onClick={() => setShowAdd(false)}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg text-sm font-semibold transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Recipients list */}
        {loading ? (
          <div className="p-12 text-center text-gray-400">Loading recipients...</div>
        ) : recipients.length === 0 ? (
          <div className="p-12 text-center">
            <Bell size={28} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-sm text-gray-400">No notification recipients configured</p>
            <p className="text-xs text-gray-400 mt-1">Add recipients to receive work order notifications</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {recipients.map((recipient) => (
              <div
                key={recipient.id}
                className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-[#264027] text-white text-sm font-bold flex items-center justify-center shrink-0">
                    {recipient.name
                      .split(" ")
                      .map((w) => w[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{recipient.name}</p>
                    <div className="flex flex-wrap items-center gap-3 mt-0.5">
                      <span className="text-xs text-gray-500 flex items-center gap-1 truncate">
                        <Mail size={10} /> {recipient.email}
                      </span>
                      {recipient.phone && (
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Phone size={10} /> {recipient.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4 shrink-0">
                  {/* Email toggle */}
                  <label className="flex items-center gap-2 cursor-pointer" title="Email notifications">
                    <Mail size={14} className={recipient.email_notifications ? "text-[#264027]" : "text-gray-300 dark:text-gray-600"} />
                    <button
                      onClick={() => toggleNotification(recipient.id, "email_notifications", !recipient.email_notifications)}
                      className={`relative w-9 h-5 rounded-full transition-colors ${
                        recipient.email_notifications
                          ? "bg-[#264027]"
                          : "bg-gray-200 dark:bg-gray-700"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                          recipient.email_notifications ? "translate-x-4" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </label>

                  {/* Phone toggle */}
                  <label className="flex items-center gap-2 cursor-pointer" title="Phone notifications">
                    <Phone size={14} className={recipient.phone_notifications ? "text-[#264027]" : "text-gray-300 dark:text-gray-600"} />
                    <button
                      onClick={() => toggleNotification(recipient.id, "phone_notifications", !recipient.phone_notifications)}
                      className={`relative w-9 h-5 rounded-full transition-colors ${
                        recipient.phone_notifications
                          ? "bg-[#264027]"
                          : "bg-gray-200 dark:bg-gray-700"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                          recipient.phone_notifications ? "translate-x-4" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </label>

                  {/* Delete */}
                  {deletingId === recipient.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(recipient.id)}
                        className="text-xs px-2 py-1 bg-red-600 text-white rounded font-semibold hover:bg-red-700 transition-colors"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setDeletingId(null)}
                        className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeletingId(recipient.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info note */}
      <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 rounded-xl p-4">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Recipients listed here will be notified when work orders are created or updated.
          Email notifications are sent immediately. Phone notifications are sent via SMS.
        </p>
      </div>
    </div>
  );
}
